/**
 * SinkholeSkillEffect — Earth tree sculptor fork 2 active (sinkhole sub-path).
 *
 * Opens a 2x2 pit at a targeted point within 4 tiles. Enemies at the impact
 * location are rooted for 1 second (hard root: blocks movement, allows attacks).
 * The pit persists as a slow zone (50% speed, 6 seconds) with dash and charge
 * suppression. No dashing, no charging from a Sinkhole.
 *
 * Upgrade keys on scope 'sinkhole':
 * - slowedBonusEntryDamage: additive (Sunken: 10) — entry damage to pre-slowed enemies
 * - radiusTiles: additive (Quarry: 1) — zone expands from 2x2 to 3x3
 * - rootDurationMs: additive (Quarry: 500) — root extends to 1500ms
 * - suppressBackup: boolean (Quarry: true) — blocks backup calls during root
 * - impassableExtensionMs: additive (Fault Memory: 3000) — zone stays impassable post-slow
 *
 * Extends InstantSkillEffect; overrides cooldown and update for timer tracking.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy — root is movement-blocking, attacks unaffected)
 * @see earth-spec.md § Sculptor Fork 2 — Sinkhole Sub-Path
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Zone config interface (extends base zone config with sinkhole-specific fields)
// ---------------------------------------------------------------------------

export interface SinkholeZoneConfig {
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly radiusTiles: number;
  readonly durationMs: number;
  readonly speedMultiplier: number;
  readonly sourceSkillId: string;
  readonly suppressDash?: boolean;
  readonly suppressCharge?: boolean;
  readonly suppressBackup?: boolean;
  readonly blocksMovement?: boolean;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SinkholeDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly targeting: {
    /** Returns the aimed target point clamped to the given range. */
    getTargetPoint(rangeTiles: number): {
      position: { x: number; y: number };
    };
    /** Returns enemy IDs within the given pixel radius of a world position. */
    getEnemiesInRadius(
      center: { x: number; y: number },
      radiusPx: number,
    ): string[];
  };
  readonly statusEffects: {
    apply(
      targetId: string,
      type: string,
      config: { durationMs: number; sourceSkillId: string },
    ): void;
    /** Returns true if the target has any slow from a source other than 'sinkhole'. */
    isSlowedByExternalSource(targetId: string): boolean;
  };
  readonly zones: {
    create(config: SinkholeZoneConfig): string;
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string },
    ): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SinkholeConfig {
  /** Base range in tiles for target placement. */
  readonly baseRangeTiles: number;
  /** Base root duration in ms (before Quarry extension). */
  readonly baseRootDurationMs: number;
  /** Total zone duration in ms (root phase + slow phase). */
  readonly zoneDurationMs: number;
  /** Slow zone speed multiplier (0.5 = 50% speed). */
  readonly slowSpeedMultiplier: number;
  /** Base zone radius in tiles (1 = 2x2 area). */
  readonly baseRadiusTiles: number;
  /** Long cooldown tier in ms. */
  readonly cooldownMs: number;
  /** Pixels per tile — converts tile distances to pixels. */
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: SinkholeConfig = {
  baseRangeTiles: 4,
  baseRootDurationMs: 1000,       // 1s hard root
  zoneDurationMs: 7000,           // 1s root + 6s slow
  slowSpeedMultiplier: 0.5,       // 50% speed inside zone
  baseRadiusTiles: 1,             // 1-tile radius = 2x2 area
  cooldownMs: 2000,               // long cooldown tier
  tileSizePx: 32,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SinkholeSkillEffect extends InstantSkillEffect {
  readonly skillId = 'sinkhole';

  private readonly deps: SinkholeDeps;
  private readonly config: SinkholeConfig;

  // Cooldown timer (ms remaining until ready)
  private _cooldownTimer = 0;

  // Per-activation telemetry (readable by simulation scenarios)
  private _activationCount = 0;
  private _rootedEnemyCount = 0;
  private _lastRootDurationMs = 0;
  private _lastZoneId: string | null = null;
  private _lastImpassableZoneId: string | null = null;
  private _lastEffectiveRadius = 0;
  private _lastTargetPosition: { x: number; y: number } | null = null;
  private _lastZoneConfig: SinkholeZoneConfig | null = null;
  private _lastImpassableZoneConfig: SinkholeZoneConfig | null = null;
  private _lastSuppressBackup = false;
  private _sunkenDamageCount = 0;

  constructor(deps: SinkholeDeps, config: Partial<SinkholeConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  override get cooldown(): number { return this._cooldownTimer; }
  get activationCount(): number { return this._activationCount; }
  get rootedEnemyCount(): number { return this._rootedEnemyCount; }
  get lastRootDurationMs(): number { return this._lastRootDurationMs; }
  get lastZoneId(): string | null { return this._lastZoneId; }
  get lastImpassableZoneId(): string | null { return this._lastImpassableZoneId; }
  get lastEffectiveRadius(): number { return this._lastEffectiveRadius; }
  get lastTargetPosition(): { x: number; y: number } | null { return this._lastTargetPosition; }
  get lastZoneConfig(): SinkholeZoneConfig | null { return this._lastZoneConfig; }
  get lastImpassableZoneConfig(): SinkholeZoneConfig | null { return this._lastImpassableZoneConfig; }
  get lastSuppressBackup(): boolean { return this._lastSuppressBackup; }
  get sunkenDamageCount(): number { return this._sunkenDamageCount; }

  // --- InstantSkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownTimer <= 0;
  }

  activate(): boolean {
    if (this._cooldownTimer > 0) return false;

    // --- Resolve upgrade deltas from UpgradeEffectResolver ---
    const radiusDelta =
      this.deps.resolver.getAdditiveEffect('sinkhole', 'radiusTiles');
    const rootDurationDelta =
      this.deps.resolver.getAdditiveEffect('sinkhole', 'rootDurationMs');
    const suppressBackup =
      this.deps.resolver.hasEffect('sinkhole', 'suppressBackup');
    const impassableExtensionMs =
      this.deps.resolver.getAdditiveEffect('sinkhole', 'impassableExtensionMs');

    const effectiveRadius = this.config.baseRadiusTiles + radiusDelta;
    const effectiveRootDurationMs = this.config.baseRootDurationMs + rootDurationDelta;

    this._lastEffectiveRadius = effectiveRadius;
    this._lastRootDurationMs = effectiveRootDurationMs;
    this._lastSuppressBackup = suppressBackup;

    // --- Get target point ---
    const { position } =
      this.deps.targeting.getTargetPoint(this.config.baseRangeTiles);
    this._lastTargetPosition = position;

    // --- Root enemies at the target position ---
    const radiusPx = effectiveRadius * this.config.tileSizePx;
    const enemies = this.deps.targeting.getEnemiesInRadius(position, radiusPx);
    this._rootedEnemyCount = enemies.length;

    for (const enemyId of enemies) {
      this.deps.statusEffects.apply(enemyId, 'root', {
        durationMs: effectiveRootDurationMs,
        sourceSkillId: 'sinkhole',
      });
    }

    // --- Create slow zone ---
    const zoneConfig: SinkholeZoneConfig = {
      type: 'sinkhole',
      position,
      radiusTiles: effectiveRadius,
      durationMs: this.config.zoneDurationMs,
      speedMultiplier: this.config.slowSpeedMultiplier,
      sourceSkillId: 'sinkhole',
      suppressDash: true,
      suppressCharge: true,
      suppressBackup,
    };

    this._lastZoneConfig = zoneConfig;
    this._lastZoneId = this.deps.zones.create(zoneConfig);

    // --- Fault Memory: create impassable extension zone ---
    if (impassableExtensionMs > 0) {
      const impassableConfig: SinkholeZoneConfig = {
        type: 'sinkhole_impassable',
        position,
        radiusTiles: effectiveRadius,
        durationMs: this.config.zoneDurationMs + impassableExtensionMs,
        speedMultiplier: 1.0,
        sourceSkillId: 'sinkhole',
        blocksMovement: true,
      };
      this._lastImpassableZoneConfig = impassableConfig;
      this._lastImpassableZoneId = this.deps.zones.create(impassableConfig);
    } else {
      this._lastImpassableZoneId = null;
      this._lastImpassableZoneConfig = null;
    }

    // --- Start cooldown ---
    this._cooldownTimer = this.config.cooldownMs;
    this._activationCount++;

    return true;
  }

  /**
   * Called by ZoneSystem when an entity enters the sinkhole zone.
   * Handles Sunken upgrade: deals bonus damage to enemies already slowed
   * by an external source (not the sinkhole itself).
   */
  onEntityEnterZone(entityId: string): void {
    const entryDamage =
      this.deps.resolver.getAdditiveEffect('sinkhole', 'slowedBonusEntryDamage');
    if (entryDamage <= 0) return;

    if (this.deps.statusEffects.isSlowedByExternalSource(entityId)) {
      this.deps.combat.applyDamage(entityId, entryDamage, {
        sourceSkillId: 'sinkhole',
      });
      this._sunkenDamageCount++;
    }
  }

  override update(delta: number): void {
    if (this._cooldownTimer > 0) {
      this._cooldownTimer -= delta;
      if (this._cooldownTimer < 0) this._cooldownTimer = 0;
    }
  }
}
